import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {
    Card, CardContent, Typography, Grid, Box, Button, Chip,
    CircularProgress, Alert, IconButton, Avatar, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Breadcrumbs, Link, Skeleton
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SportsIcon from '@mui/icons-material/Sports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import RefreshIcon from '@mui/icons-material/Refresh';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';
import { TeamPlayers } from './tabs/TeamsPlayers';
import { TeamMatches } from './tabs/TeamsMetchs';
import { TeamActivityLog } from './tabs/TeamActivityLog';
import { TeamPenalties } from './tabs/TeamPenalties';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export const SquadreDetailsPage = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati
    const [team, setTeam] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [updating, setUpdating] = React.useState(false);

    // Tab state
    const [value, setValue] = React.useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };


    // Carica i dettagli della squadra
    const loadTeamDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/v-team`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        teamId: parseInt(teamId),
                        $limit: 1
                    }
                }
            );

            const data = response.data;
            if (data.data && data.data.length > 0) {
                setTeam(data.data[0]);
            } else {
                setError("Squadra non trovata");
            }

        } catch (error) {
            console.error("Errore nel recupero dettagli squadra:", error);
            setError("Errore nel caricamento dei dettagli della squadra");
            notify("Errore nel caricamento dei dettagli", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Gestisce il toggle visibilità
    const handleToggleVisibility = async () => {
        try {
            setUpdating(true);
            const jwt = getJwt();

            await axios.patch(
                `${host}/tbl-team/${team.teamId}`,
                { flVisibile: !team.flVisibile },
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            notify(`Squadra ${team.flVisibile ? 'nascosta' : 'mostrata'}`, { type: 'success' });

            // Aggiorna lo stato locale
            setTeam(prev => ({
                ...prev,
                flVisibile: !prev.flVisibile
            }));

        } catch (error) {
            console.error("Errore nell'aggiornamento:", error);
            notify("Errore nell'aggiornamento", { type: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    // Gestisce la modifica (placeholder)
    const handleEdit = () => {
        notify(`(DISATTIVO) Modifica squadra: ${team.teamNome}`, { type: 'info' });
    };

    // Carica i dati al mount e quando cambia teamId
    React.useEffect(() => {
        if (teamId) {
            loadTeamDetails();
        }
    }, [teamId]);

    // Calcola lo stato della squadra
    const getTeamStatus = () => {
        if (!team) return { label: 'N/A', color: '#9e9e9e', bgcolor: '#f5f5f5' };

        if (team.flAnnullato) {
            return { label: 'Annullata', color: '#f44336', bgcolor: '#ffebee' };
        } else if (!team.flAttivo) {
            return { label: 'Inattiva', color: '#ff9800', bgcolor: '#fff3e0' };
        } else if (!team.flVisibile) {
            return { label: 'Nascosta', color: '#9e9e9e', bgcolor: '#f5f5f5' };
        } else {
            return { label: 'Attiva', color: '#4caf50', bgcolor: '#e8f5e8' };
        }
    };

    const status = getTeamStatus();

    // Loading skeleton
    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                    {/* Breadcrumb skeleton */}
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={300} height={24} />
                    </Box>

                    {/* Header skeleton */}
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={80} height={80} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width={200} height={32} />
                            <Skeleton variant="text" width={150} height={20} />
                        </Box>
                    </Box>

                    {/* Cards skeleton */}
                    <Grid container spacing={3}>
                        {[1, 2, 3, 4].map(i => (
                            <Grid item xs={12} md={6} key={i}>
                                <Card sx={{ p: 3 }}>
                                    <Skeleton variant="text" width={120} height={24} />
                                    <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>

                    {/* Breadcrumb */}
                    <Breadcrumbs sx={{ mb: 3 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/squadre')}
                            sx={{ textDecoration: 'none' }}
                        >
                            Squadre
                        </Link>
                        <Typography variant="body2" color="text.primary">
                            Errore
                        </Typography>
                    </Breadcrumbs>

                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={loadTeamDetails}>
                                Riprova
                            </Button>
                        }
                    >
                        {error}
                    </Alert>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/squadre')}
                    >
                        Torna alla lista
                    </Button>
                </motion.div>
            </Box>
        );
    }

    // Main content
    return (
        <Box sx={{ width: '100%' }}>
            {/* Breadcrumb */}
            <Breadcrumbs sx={{ mb: 3 }}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/squadre')}
                    sx={{ textDecoration: 'none' }}
                >
                    Squadre
                </Link>
                <Typography variant="body2" color="text.primary">
                    {team?.teamNome || `Squadra ${teamId}`}
                </Typography>
            </Breadcrumbs>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                    <Tab label="Info Base" {...a11yProps(0)} />
                    <Tab label="Giocatori" {...a11yProps(1)} />
                    <Tab label="Partite" {...a11yProps(2)} />
                    <Tab label="Log Attività" {...a11yProps(3)} />
                    <Tab label="⚠️ Penalità" {...a11yProps(4)} />
                </Tabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
                <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {/* Header con info principale */}
                        <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>

                                {/* Avatar/Logo */}
                                <Avatar
                                    src={team?.foto ? `${host}/image-proxy-mw?url=${encodeURIComponent(team.foto)}` : undefined}
                                    alt={team?.teamNome}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        bgcolor: team?.foto ? 'transparent' : 'primary.main',
                                        fontSize: '2rem'
                                    }}
                                >
                                    {!team?.foto && team?.teamNome ?
                                        team.teamNome.charAt(0).toUpperCase() :
                                        '⚽'
                                    }
                                </Avatar>

                                {/* Info principale */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                        {team?.teamNome || 'Nome non disponibile'}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={status.label}
                                            sx={{
                                                color: status.color,
                                                bgcolor: status.bgcolor,
                                                fontWeight: 500
                                            }}
                                        />
                                        <Chip
                                            icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
                                            label={team?.legaDescrizione || `Lega ${team?.legaId}`}
                                            sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }}
                                        />
                                        <Chip
                                            icon={<SportsIcon sx={{ fontSize: 16 }} />}
                                            label={team?.sportNome || 'Sport N/A'}
                                            sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500 }}
                                        />
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        ID Squadra: {team?.teamId}
                                    </Typography>

                                    {team?.note && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontStyle: 'italic',
                                                color: 'text.secondary',
                                                bgcolor: '#f5f5f5',
                                                p: 1.5,
                                                borderRadius: 1,
                                                borderLeft: '4px solid #2196f3'
                                            }}
                                        >
                                            {team.note}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Azioni */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => navigate('/squadre')}
                                        size="small"
                                    >
                                        Indietro
                                    </Button>

                                    <Button
                                        variant="contained"
                                        startIcon={<EditIcon />}
                                        // onClick={handleEdit}
                                        onClick={() => navigate(`/squadre/${teamId}/edit`)}
                                        size="small"
                                    >
                                        Modifica
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        startIcon={team?.flVisibile ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        onClick={handleToggleVisibility}
                                        disabled={updating}
                                        size="small"
                                        sx={{
                                            color: team?.flVisibile ? 'text.primary' : '#ed6c02',
                                            borderColor: team?.flVisibile ? 'grey.300' : '#ed6c02',
                                            '&:hover': {
                                                borderColor: team?.flVisibile ? 'grey.400' : '#e65100',
                                                bgcolor: team?.flVisibile ? 'grey.50' : 'rgba(237, 108, 2, 0.04)'
                                            }
                                        }}
                                    >
                                        {updating ? <CircularProgress size={16} /> : (team?.flVisibile ? 'Nascondi' : 'Mostra')}
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={loadTeamDetails}
                                        size="small"
                                    >
                                        Aggiorna
                                    </Button>
                                </Box>
                            </Box>
                        </Card>

                        {/* Dettagli organizzati in cards */}
                        <Grid container spacing={3}>

                            {/* Informazioni Squadra */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <InfoIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Informazioni Squadra
                                        </Typography>
                                    </Box>

                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '40%' }}>Nome</TableCell>
                                                <TableCell>{team?.teamNome || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>ID Squadra</TableCell>
                                                <TableCell>{team?.teamId}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Ordinamento</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <StarIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                        {team?.ordinamento || 'Non impostato'}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Lega */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <EmojiEventsIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Lega
                                        </Typography>
                                    </Box>

                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '40%' }}>Nome</TableCell>
                                                <TableCell>{team?.legaDescrizione || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>ID Lega</TableCell>
                                                <TableCell>{team?.legaId}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Sport */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <SportsIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Sport
                                        </Typography>
                                    </Box>

                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '40%' }}>Nome</TableCell>
                                                <TableCell>{team?.sportNome || 'N/A'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>ID Sport</TableCell>
                                                <TableCell>{team?.sportId}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Stato e Flags */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <VisibilityIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Stato e Visibilità
                                        </Typography>
                                    </Box>

                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500, width: '40%' }}>Visibile</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={team?.flVisibile ? 'Sì' : 'No'}
                                                        size="small"
                                                        color={team?.flVisibile ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Attiva</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={team?.flAttivo ? 'Sì' : 'No'}
                                                        size="small"
                                                        color={team?.flAttivo ? 'success' : 'warning'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Annullata</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={team?.flAnnullato ? 'Sì' : 'No'}
                                                        size="small"
                                                        color={team?.flAnnullato ? 'error' : 'success'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Card>
                            </Grid>

                            {/* Timestamp */}
                            <Grid item xs={12}>
                                <Card sx={{ p: 3, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <CalendarTodayIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Timestamp
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Table size="small">
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 500, width: '40%' }}>Creata il</TableCell>
                                                        <TableCell>
                                                            {team?.createdAt ?
                                                                new Date(team.createdAt).toLocaleString('it-IT') :
                                                                'N/A'
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Table size="small">
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 500, width: '40%' }}>Aggiornata il</TableCell>
                                                        <TableCell>
                                                            {team?.updatedAt ?
                                                                new Date(team.updatedAt).toLocaleString('it-IT') :
                                                                'N/A'
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Footer con ultimo aggiornamento */}
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                            </Typography>
                        </Box>

                    </motion.div>
                </Box>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
                <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '50vh' }}>
                    <TeamPlayers teamId={teamId} />
                </Box>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
                <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '50vh' }}>
                    <TeamMatches
                        teamId={teamId}
                        teamName={team?.teamNome}
                    />
                </Box>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3}>
                <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '50vh' }}>
                    <TeamActivityLog
                        teamId={teamId}
                        teamName={team?.teamNome}
                    />
                </Box>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={4}>
                <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '50vh' }}>
                    <TeamPenalties teamId={teamId} />
                </Box>
            </CustomTabPanel>
        </Box>

    );
};